import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Edit, Trash2, Save, X, ArrowLeft, Youtube, Globe, 
  Rss, Podcast, ExternalLink, GripVertical, Eye, EyeOff,
  Star, StarOff, Video, FileText, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_TYPES = [
  { id: 'youtube_channel', label: 'YouTube Channel', icon: Youtube },
  { id: 'youtube_playlist', label: 'YouTube Playlist', icon: Youtube },
  { id: 'website', label: 'Website', icon: Globe },
  { id: 'rss_feed', label: 'RSS Feed', icon: Rss },
  { id: 'podcast', label: 'Podcast', icon: Podcast },
  { id: 'social_media', label: 'Social Media', icon: Globe },
];

const CATEGORIES = [
  { id: 'training', label: 'Training' },
  { id: 'health', label: 'Health' },
  { id: 'nutrition', label: 'Nutrition' },
  { id: 'grooming', label: 'Grooming' },
  { id: 'behavior', label: 'Behavior' },
  { id: 'news', label: 'News' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'rescue', label: 'Rescue & Adoption' },
  { id: 'general', label: 'General' },
];

// Extract YouTube video ID from various URL formats
const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
    /youtube\.com\/v\/([^?&]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Generate YouTube thumbnail URL from video ID
const getYouTubeThumbnail = (url) => {
  const videoId = extractYouTubeVideoId(url);
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return null;
};

const FeedSourcesAdmin = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams(); // Get club slug from URL if present
  const [sources, setSources] = useState([]);
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sources'); // 'sources' or 'items'
  const [showEditor, setShowEditor] = useState(false);
  const [editorType, setEditorType] = useState('source'); // 'source' or 'item'
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  // Source form state
  const [sourceForm, setSourceForm] = useState({
    name: '',
    description: '',
    source_type: 'youtube_channel',
    category: 'general',
    url: '',
    feed_url: '',
    thumbnail_url: '',
    is_active: true,
    is_featured: false,
    display_order: 0
  });

  // Feed item form state
  const [itemForm, setItemForm] = useState({
    title: '',
    description: '',
    url: '',
    thumbnail_url: '',
    video_url: '',
    is_video: false,
    source_name: '',
    category: 'general'
  });

  useEffect(() => {
    // Check if user is club officer or admin
    const isOfficer = user?.is_admin || user?.is_operator_admin || 
                      ['club_officer', 'ngo_officer'].includes(user?.member_class);
    if (!isOfficer) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch sources
      const sourcesRes = await fetch(`${API}/api/feed/sources?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sourcesRes.ok) {
        const data = await sourcesRes.json();
        setSources(data);
      }

      // Fetch feed items (we'll need to add this endpoint)
      // For now, just use an empty array
      setFeedItems([]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openSourceEditor = (source = null) => {
    if (source) {
      setEditingItem(source);
      setSourceForm({
        name: source.name,
        description: source.description || '',
        source_type: source.source_type,
        category: source.category,
        url: source.url,
        feed_url: source.feed_url || '',
        thumbnail_url: source.thumbnail_url || '',
        is_active: source.is_active,
        is_featured: source.is_featured,
        display_order: source.display_order
      });
    } else {
      setEditingItem(null);
      setSourceForm({
        name: '',
        description: '',
        source_type: 'youtube_channel',
        category: 'general',
        url: '',
        feed_url: '',
        thumbnail_url: '',
        is_active: true,
        is_featured: false,
        display_order: sources.length
      });
    }
    setEditorType('source');
    setShowEditor(true);
  };

  const openItemEditor = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemForm({
        title: item.title,
        description: item.description || '',
        url: item.url,
        thumbnail_url: item.thumbnail_url || '',
        video_url: item.video_url || '',
        is_video: item.is_video || false,
        source_name: item.source_name || '',
        category: item.category || 'general'
      });
    } else {
      setEditingItem(null);
      setItemForm({
        title: '',
        description: '',
        url: '',
        thumbnail_url: '',
        video_url: '',
        is_video: false,
        source_name: '',
        category: 'general'
      });
    }
    setEditorType('item');
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingItem(null);
  };

  const handleUrlChange = (url, formType) => {
    if (formType === 'item') {
      // Auto-detect if it's a video and generate thumbnail
      const isVideo = url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
      const thumbnail = getYouTubeThumbnail(url);
      
      setItemForm(prev => ({
        ...prev,
        url,
        is_video: isVideo,
        video_url: isVideo ? url : prev.video_url,
        thumbnail_url: thumbnail || prev.thumbnail_url
      }));
    } else {
      setSourceForm(prev => ({ ...prev, url }));
    }
  };

  const saveSource = async () => {
    setSaving(true);
    try {
      const url = editingItem 
        ? `${API}/api/feed/sources/${editingItem.id}`
        : `${API}/api/feed/sources`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(sourceForm)
      });

      if (response.ok) {
        await fetchData();
        closeEditor();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to save source'}`);
      }
    } catch (error) {
      console.error('Error saving source:', error);
      alert('Failed to save source');
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/feed/external-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(itemForm)
      });

      if (response.ok) {
        await fetchData();
        closeEditor();
        alert('Video/article added to feed!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to add item'}`);
      }
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (sourceId) => {
    if (!window.confirm('Are you sure you want to delete this source?')) return;

    try {
      const response = await fetch(`${API}/api/feed/sources/${sourceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSources(sources.filter(s => s.id !== sourceId));
      }
    } catch (error) {
      console.error('Error deleting source:', error);
    }
  };

  const toggleSourceActive = async (source) => {
    try {
      const response = await fetch(`${API}/api/feed/sources/${source.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !source.is_active })
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling source:', error);
    }
  };

  const toggleSourceFeatured = async (source) => {
    try {
      const response = await fetch(`${API}/api/feed/sources/${source.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_featured: !source.is_featured })
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const filteredSources = sources.filter(source =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    source.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSourceIcon = (type) => {
    const found = SOURCE_TYPES.find(t => t.id === type);
    return found ? found.icon : Globe;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(slug ? `/club/${slug}` : '/')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Feed Sources Manager</h1>
                <p className="text-sm text-gray-500">
                  {slug ? `Manage curated content for this club` : 'Manage curated content sources for the public feed'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => openItemEditor()}
                className="flex items-center gap-2"
              >
                <Video className="w-4 h-4" />
                Add Video/Article
              </Button>
              <Button
                onClick={() => openSourceEditor()}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Source
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 pt-6">
        {/* Quick Links to Event & News Sources */}
        <div className="mb-4 flex gap-3">
          <button
            onClick={() => navigate('/admin/event-sources')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
          >
            <span className="w-5 h-5 flex items-center justify-center">📅</span>
            Event Sources
          </button>
          <button
            onClick={() => navigate('/admin/news-sources')}
            className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition text-sm font-medium"
          >
            <span className="w-5 h-5 flex items-center justify-center">📰</span>
            News Sources
          </button>
        </div>
        
        <div className="flex gap-4 border-b">
          <button
            onClick={() => setActiveTab('sources')}
            className={`pb-3 px-2 font-medium transition ${
              activeTab === 'sources'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Curated Sources ({sources.length})
          </button>
          <button
            onClick={() => setActiveTab('items')}
            className={`pb-3 px-2 font-medium transition ${
              activeTab === 'items'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Individual Items ({feedItems.length})
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search sources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Sources Tab */}
        {activeTab === 'sources' && (
          loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredSources.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Youtube className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No sources added yet</p>
              <Button onClick={() => openSourceEditor()}>
                Add Your First Source
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Source</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 hidden md:table-cell">Type</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 hidden lg:table-cell">Category</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Active</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Featured</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredSources.map(source => {
                    const Icon = getSourceIcon(source.source_type);
                    return (
                      <tr key={source.id} className={`hover:bg-gray-50 ${!source.is_active ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <Icon className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{source.name}</p>
                              <a 
                                href={source.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                              >
                                Visit <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-gray-600 capitalize">
                            {source.source_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full capitalize">
                            {source.category}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleSourceActive(source)}
                            className={`p-1.5 rounded-lg transition ${
                              source.is_active
                                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                          >
                            {source.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleSourceFeatured(source)}
                            className={`p-1.5 rounded-lg transition ${
                              source.is_featured
                                ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                          >
                            {source.is_featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openSourceEditor(source)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteSource(source.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Items Tab */}
        {activeTab === 'items' && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">Add individual videos or articles to the feed</p>
            <p className="text-sm text-gray-400 mb-4">
              These will appear in the public feed alongside content from your curated sources
            </p>
            <Button onClick={() => openItemEditor()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Video or Article
            </Button>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 my-auto">
            {/* Editor Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editorType === 'source' 
                  ? (editingItem ? 'Edit Source' : 'Add New Source')
                  : 'Add Video or Article'
                }
              </h2>
              <button onClick={closeEditor} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Editor Form */}
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {editorType === 'source' ? (
                <>
                  {/* Source Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={sourceForm.name}
                      onChange={(e) => setSourceForm({...sourceForm, name: e.target.value})}
                      placeholder="e.g., Zak George's Dog Training"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                    <input
                      type="url"
                      value={sourceForm.url}
                      onChange={(e) => setSourceForm({...sourceForm, url: e.target.value})}
                      placeholder="https://youtube.com/@channel or website URL"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={sourceForm.source_type}
                        onChange={(e) => setSourceForm({...sourceForm, source_type: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {SOURCE_TYPES.map(type => (
                          <option key={type.id} value={type.id}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={sourceForm.category}
                        onChange={(e) => setSourceForm({...sourceForm, category: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={sourceForm.description}
                      onChange={(e) => setSourceForm({...sourceForm, description: e.target.value})}
                      placeholder="Brief description of this source"
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sourceForm.is_active}
                        onChange={(e) => setSourceForm({...sourceForm, is_active: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sourceForm.is_featured}
                        onChange={(e) => setSourceForm({...sourceForm, is_featured: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Featured</span>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {/* Item Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                    <input
                      type="url"
                      value={itemForm.url}
                      onChange={(e) => handleUrlChange(e.target.value, 'item')}
                      placeholder="https://youtu.be/VIDEO_ID or article URL"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      YouTube thumbnails are auto-generated from the URL
                    </p>
                  </div>

                  {itemForm.thumbnail_url && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                      <img 
                        src={itemForm.thumbnail_url} 
                        alt="Thumbnail preview" 
                        className="w-48 h-auto rounded-lg border"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={itemForm.title}
                      onChange={(e) => setItemForm({...itemForm, title: e.target.value})}
                      placeholder="Video or article title"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={itemForm.description}
                      onChange={(e) => setItemForm({...itemForm, description: e.target.value})}
                      placeholder="Brief description"
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
                      <input
                        type="text"
                        value={itemForm.source_name}
                        onChange={(e) => setItemForm({...itemForm, source_name: e.target.value})}
                        placeholder="e.g., Stonnie Dennis"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={itemForm.category}
                        onChange={(e) => setItemForm({...itemForm, category: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={itemForm.is_video}
                      onChange={(e) => setItemForm({...itemForm, is_video: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">This is a video</span>
                  </label>
                </>
              )}
            </div>

            {/* Editor Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <Button variant="outline" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                onClick={editorType === 'source' ? saveSource : saveItem}
                disabled={saving || (editorType === 'source' ? !sourceForm.name || !sourceForm.url : !itemForm.title || !itemForm.url)}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editorType === 'source' ? (editingItem ? 'Update Source' : 'Add Source') : 'Add to Feed'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedSourcesAdmin;
